import type { ICalendarEvent } from '@/features/calendar/interfaces'
import { differenceInCalendarDays, endOfDay, endOfWeek, isSameDay, parseISO, startOfDay, startOfWeek } from 'date-fns'
import { EventDetailsDialog } from '@/features/calendar/components/dialogs/event-details-dialog'
import { cn } from '@/lib/utils'

type Props = {
  weekStart: Date
  weekEnd: Date
  trainings: ICalendarEvent[]
  trainingPositions: Record<string, number>
}

const colorClass: Record<string, string> = {
    blue: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-100',
    green: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-100',
    red: 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-100',
    yellow: 'border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-100',
    purple: 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-100',
    orange: 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-100',
}

export function MonthWeekSpansRow({ weekStart, weekEnd, trainings, trainingPositions }: Props) {
  const wStart = startOfWeek(weekStart)
  const wEnd = endOfWeek(weekEnd)

  const spans = trainings
    .filter((t) => {
      const s = startOfDay(parseISO(t.startAt))
      const e = endOfDay(parseISO(t.endAt))
      return s <= wEnd && e >= wStart
    })
    .map((t) => {
      const s = startOfDay(parseISO(t.startAt))
      const e = endOfDay(parseISO(t.endAt))
      const segStart = s < wStart ? wStart : s
      const segEnd = e > wEnd ? wEnd : e
      const startCol = segStart.getDay() // 0..6 Sun..Sat
      const spanCols = differenceInCalendarDays(segEnd, segStart) + 1
      const lane = trainingPositions[t.id] ?? 0
      const roundLeft = isSameDay(segStart, s)
      const roundRight = isSameDay(segEnd, e)
      return { training: t, startCol, spanCols, lane, roundLeft, roundRight }
    })

  if (spans.length === 0)
    return null

  return (
    <div className="pointer-events-none absolute left-0 right-0 top-[36px] h-[72px] grid grid-cols-7 grid-rows-3 gap-x-1">
      {spans.map(({ training, startCol, spanCols, lane, roundLeft, roundRight }) => (
        <EventDetailsDialog
          key={`${training.id}-${startCol}`}
          training={training}
        >
          <div
            className={cn(
              'pointer-events-auto flex h-4.5 items-center justify-between truncate whitespace-nowrap rounded-md border px-2 text-xs',
              colorClass[training.color] ?? colorClass.blue,
              roundLeft ? 'rounded-l-md' : 'rounded-l-none',
              roundRight ? 'rounded-r-md' : 'rounded-r-none',
            )}
            // eslint-disable-next-line react/forbid-dom-props
            style={{ gridColumn: `${startCol + 1} / span ${spanCols}`, gridRow: `${lane + 1}` }}
          >
            <p className="flex-1 truncate font-semibold">{training.title}</p>
            {/* Keep end time on the right for multi-day */}
            <span>Starts: {new Date(training.startAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </EventDetailsDialog>
      ))}
    </div>
  )
}
